/**
 * @lines98/client/components/theme-switch — сегментный переключатель темы
 * приложения: ☀ светлая (`retro`) / 🌙 тёмная (`dark`). ТОЛЬКО ИКОНКИ, без подписей
 * (owner-decision 2026-06-04, §5.5).
 *
 * ОБЩИЙ контрол-модуль (CLAUDE.md «код через модули»): сейчас потребитель —
 * `lib/settings-list` (строка «Тема»). Оба варианта видны сразу (сегмент, НЕ
 * дропдаун — UX-практика для 2–4 опций). Выбранный сегмент — accent-заливка (один
 * канал эмфазиса, контраст ≥ AA — токены `--accent`/`#fff` как `.btn.primary`).
 *
 * Доступность: `role="group"` + два `<button aria-pressed>`; клавиатура — нативная
 * (Tab между кнопками, Enter/Space выбирает). Каждая кнопка несёт `aria-label`
 * («Светлая тема» / «Тёмная тема»), т.к. видимой подписи нет.
 *
 * Размеры — формулами от :root (`--btn-h`/`--m`/`--fs-*`); без magic-px. Анимация —
 * только background/opacity (GPU-friendly, ui-perf skill); `prefers-reduced-motion`
 * гасит в CSS. CSS — co-located `theme-switch.css`.
 */

import './theme-switch.css';

import { t } from '../lib/i18n/index.js';
import type { AppTheme } from '../lib/theme.js';

// Иконки в стиле nav-rail (stroke currentColor, viewBox 24). Солнце — круг + лучи;
// луна — полумесяц (Tabler-подобные).
const ICON_SUN = `
  <svg class="theme-switch-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>`;

const ICON_MOON = `
  <svg class="theme-switch-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
    <path d="M12 3a6.5 6.5 0 0 0 9 9 9 9 0 1 1-9-9z" />
  </svg>`;

interface Segment {
  readonly theme: AppTheme;
  readonly icon: string;
  /** i18n-ключ aria-подписи — резолвится в `t()` ПРИ mount (см. createThemeSwitch),
   *  чтобы переключатель брал активный язык (re-mount фазы на смену locale). */
  readonly ariaKey: string;
}

const SEGMENTS: readonly Segment[] = [
  { theme: 'retro', icon: ICON_SUN, ariaKey: 'game.settingsScreen.themeLight' },
  { theme: 'dark', icon: ICON_MOON, ariaKey: 'game.settingsScreen.themeDark' },
];

export interface ThemeSwitchOptions {
  /** Текущая тема (для подсветки активного сегмента). */
  readonly value: AppTheme;
  /** Вызывается при выборе пользователем (НЕ для уже выбранного). */
  readonly onChange: (theme: AppTheme) => void;
}

export interface ThemeSwitchHandle {
  /** Корневой элемент (group) — потребитель вставляет в слот контрола. */
  readonly element: HTMLElement;
  /** Программно синхронизировать подсветку без вызова onChange. */
  setValue(theme: AppTheme): void;
}

export function createThemeSwitch(opts: ThemeSwitchOptions): ThemeSwitchHandle {
  let value = opts.value;

  const group = document.createElement('div');
  group.className = 'theme-switch';
  group.setAttribute('role', 'group');
  group.setAttribute('aria-label', t('game.settingsScreen.themeAria'));

  const buttons = new Map<AppTheme, HTMLButtonElement>();

  const applyState = (): void => {
    for (const [theme, btn] of buttons) {
      const on = theme === value;
      btn.classList.toggle('theme-switch-seg--on', on);
      btn.setAttribute('aria-pressed', String(on));
    }
  };

  for (const seg of SEGMENTS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'theme-switch-seg';
    // `t()` резолвится здесь (mount-time) → активный язык; фаза «settings» (и board,
    // несущий drawer) ре-монтируется на смену locale → подпись обновляется.
    btn.setAttribute('aria-label', t(seg.ariaKey));
    btn.innerHTML = seg.icon;
    btn.addEventListener('click', () => {
      if (value === seg.theme) return;
      value = seg.theme;
      applyState();
      opts.onChange(seg.theme);
    });
    buttons.set(seg.theme, btn);
    group.appendChild(btn);
  }

  applyState();

  return {
    element: group,
    setValue(theme: AppTheme): void {
      if (theme === value) return;
      value = theme;
      applyState();
    },
  };
}
